import * as i0 from '@angular/core';
import { Injectable, Inject, Directive, TemplateRef, Component, Optional, Input, ContentChild, NgModule } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { DomPortalOutlet, TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { Subject, Observable, merge, from, fromEvent } from 'rxjs';
import { take, map, filter, switchMap } from 'rxjs/operators';
import * as i2 from '@angular/cdk/overlay';
import { OverlayConfig } from '@angular/cdk/overlay';
import { ESCAPE, ARROW_UP } from '@anthem/uxd/util';

class OverlayContainerService {
    constructor(_document) {
        this._document = _document;
    }
    getContainer() {
        if (!this._container) {
            this._container = this._createContainerEl();
        }
        return this._container;
    }
    _createContainerEl() {
        const overlayContainer = this._document.createElement('div');
        overlayContainer.classList.add('overlay-container');
        return overlayContainer;
    }
    ngOnDestroy() {
        if (this._container && this._container.parentNode) {
            this._container.parentNode.removeChild(this._container);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayContainerService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayContainerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayContainerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/** Coerces a value to a CSS pixel value. */
function coerceCssPixelValue(value) {
    if (value == null) {
        return '';
    }
    return typeof value === 'string' ? value : `${value}px`;
}
class OverlayRef {
    getDirection() {
        const direction = this._direction;
        if (!direction) {
            return 'ltr';
        }
        return direction;
    }
    /** Gets the current overlay configuration, which is immutable. */
    getConfig() {
        return this._config;
    }
    setDirection(dir) {
        this._direction = dir;
    }
    /** The overlay's HTML element */
    get overlayElement() {
        return this._pane;
    }
    constructor(_outlet, _host, _pane, _config, _overlayWrapper, _ngZone, _keyboardDispatcher, _document) {
        this._outlet = _outlet;
        this._host = _host;
        this._pane = _pane;
        this._config = _config;
        this._overlayWrapper = _overlayWrapper;
        this._ngZone = _ngZone;
        this._keyboardDispatcher = _keyboardDispatcher;
        this._document = _document;
        this._outsidePointerEvents = null;
        this._direction = 'ltr';
        /* Click events on the backdrop */
        this._backdropClick = new Subject();
        this._keydownEventsObservable = new Observable((observer) => {
            const subscription = this._keydownEvents.subscribe(observer);
            this._keydownEventSubscriptions++;
            return () => {
                subscription.unsubscribe();
                this._keydownEventSubscriptions--;
            };
        });
        this._keydownEvents = new Subject();
        this._keydownEventSubscriptions = 0;
    }
    /** Gets an observable that emits when the backdrop has been clicked. */
    backdropClick() {
        return this._backdropClick.asObservable();
    }
    /** Gets an observable of keydown events targeted to this overlay. */
    keydownEvents() {
        return this._keydownEventsObservable;
    }
    get hostElement() {
        return this._host;
    }
    /**
     * Attaches content, given via a Portal, to the overlay.
     * If the overlay is configured to have a backdrop, it will be created.
     *
     * @param portal Portal instance to which to attach the overlay.
     * @returns The portal attachment result.
     */
    attach(portal, positioner) {
        let attachResult = this._outlet.attach(portal);
        if (this._config.hasBackdrop) {
            this._backgdropEl = this._attachBackdrop(this._config.backdropClass);
        }
        // Update the pane element with the given configuration.
        if (!this._host.parentElement && this._previousHostParent) {
            this._previousHostParent.appendChild(this._host);
        }
        if (positioner) {
            this._positioner = positioner;
            this._positioner.attach(this);
        }
        this._updateElementSize();
        // Update the position once the zone is stable so that the overlay will be fully rendered
        // before attempting to position it, as the position may depend on the size of the rendered
        // content.
        this._ngZone.onStable
            .asObservable()
            .pipe(take(1))
            .subscribe(() => {
            if (this.hasAttached()) {
                this.updatePosition();
            }
        });
        this._keyboardDispatcher.add(this);
        this._togglePointerEvents(true);
        return attachResult;
    }
    detach() {
        if (!this.hasAttached()) {
            return;
        }
        this._togglePointerEvents(false);
        this._keyboardDispatcher.remove(this);
        if (this._config.hasBackdrop) {
            this._detachBackdrop();
        }
        this._deteachOverlayWrapperWhenStable();
        this._outlet.detach();
    }
    dispose() {
        const isAttached = this.hasAttached();
        if (this._positioner) {
            this._positioner.dispose();
        }
        if (this._host && this._host.parentNode) {
            this._host.parentNode.removeChild(this._host);
            this._host = null;
        }
        this._previousHostParent = this._pane = null;
        this._backdropClick.complete();
        this._keydownEvents.complete();
        this._outlet.dispose();
        this._keyboardDispatcher.remove(this);
    }
    hasAttached() {
        return this._outlet.hasAttached();
    }
    /** Updates the position of the overlay based on the position strategy. */
    updatePosition() {
        if (this._positioner) {
            this._positioner.apply();
        }
    }
    /** Updates the size of the overlay element based on the overlay config. */
    _updateElementSize() {
        const style = this._pane.style;
        style.width = coerceCssPixelValue(this._config.width);
        style.height = coerceCssPixelValue(this._config.height);
        style.minWidth = coerceCssPixelValue(this._config.minWidth);
        style.minHeight = coerceCssPixelValue(this._config.minHeight);
        style.maxWidth = coerceCssPixelValue(this._config.maxWidth);
        style.maxHeight = coerceCssPixelValue(this._config.maxHeight);
    }
    /** Toggles the pointer events for the overlay pane element. */
    _togglePointerEvents(enablePointer) {
        this._pane.style.pointerEvents = enablePointer ? 'auto' : 'none';
    }
    _attachBackdrop(customClass) {
        const fadeInClass = 'fade-in';
        const backdrop = this._document.createElement('div');
        backdrop.classList.add('overlay-backdrop');
        if (customClass) {
            backdrop.classList.add(customClass);
        }
        this._host.insertBefore(backdrop, this._overlayWrapper);
        backdrop.addEventListener('click', (event) => {
            this._backdropClick.next(event);
        });
        if (typeof requestAnimationFrame !== 'undefined') {
            this._ngZone.runOutsideAngular(() => {
                requestAnimationFrame(() => {
                    if (backdrop) {
                        backdrop.classList.add(fadeInClass);
                    }
                });
            });
        }
        else {
            backdrop.classList.add(fadeInClass);
        }
        return backdrop;
    }
    _detachBackdrop() {
        let backdrop = this._backgdropEl;
        backdrop.classList.remove('fade-in');
        let timeoutId;
        let deleteBackdrop = () => {
            if (backdrop && backdrop.parentNode) {
                backdrop.parentNode.removeChild(backdrop);
                backdrop = null;
            }
            clearTimeout(timeoutId);
        };
        timeoutId = this._ngZone.runOutsideAngular(() => window.setTimeout(deleteBackdrop, 500));
    }
    _deteachOverlayWrapperWhenStable() {
        this._ngZone.runOutsideAngular(() => {
            const subscription = this._ngZone.onStable.asObservable().subscribe(() => {
                if (this._host && this._host.parentElement) {
                    this._previousHostParent = this._host.parentElement;
                    this._previousHostParent.removeChild(this._host);
                }
                subscription.unsubscribe();
            });
        });
    }
}

let nextUniqueId = 0;
class OverlayService {
    /**
     * Gets a position builder that can be used, via fluent API,
     * to construct and configure a position strategy.
     * @returns An overlay position builder.
     */
    position() {
        return this._positionBuilder;
    }
    constructor(_overlayContainer, _keyboardDispatcher, _applicationRef, _ngZone, _injector, _positionBuilder, _componentFactoryResolver, _renderer, _document) {
        this._overlayContainer = _overlayContainer;
        this._keyboardDispatcher = _keyboardDispatcher;
        this._applicationRef = _applicationRef;
        this._ngZone = _ngZone;
        this._injector = _injector;
        this._positionBuilder = _positionBuilder;
        this._componentFactoryResolver = _componentFactoryResolver;
        this._renderer = _renderer;
        this._document = _document;
        this.renderer = _renderer.createRenderer(null, null);
    }
    create(config) {
        /** <body> element */
        const bodyEl = this._document.body;
        /** Creates a new <div class="overlay-container"> element */
        this._hostEl = this._createOverlayHostEl();
        /** Create a new <div class="overlay-wrapper"> element
         *  and appends to _hostEl */
        this._overlayEl = this._createOverlayWrapperEl(this._hostEl);
        /** Create a new <div class="uxd-overlay-pane" id="uxd-overlay-0"> element
         *  and append to _overlayEl
         */
        const pane = this._createPaneElement(this._overlayEl);
        const portalOutlet = this._createDOMPortalOutlet(pane);
        /** Creates a new overlay config object with given config */
        const overlayConfig = new OverlayConfig(config);
        /** Appends the <div class="overlay-container"> element to Body element */
        bodyEl.appendChild(this._hostEl);
        /** Creates a new overlay reference with parameters */
        return new OverlayRef(portalOutlet, this._hostEl, pane, overlayConfig, this._overlayEl, this._ngZone, this._keyboardDispatcher, this._document);
    }
    get hostElement() {
        return this._hostEl;
    }
    get overlayElement() {
        return this._overlayEl;
    }
    /**
     * Creates the DOM element for an overlay and appends it to the overlay container.
     * @returns Newly-created pane element
     */
    _createPaneElement(host) {
        const pane = this._document.createElement('div');
        pane.id = `uxd-overlay-${nextUniqueId++}`;
        pane.classList.add('uxd-overlay-pane');
        host.appendChild(pane);
        return pane;
    }
    _createOverlayHostEl() {
        return this._overlayContainer.getContainer();
    }
    _createOverlayWrapperEl(host) {
        const overlayWindow = this._document.createElement('div');
        overlayWindow.classList.add('overlay-wrapper');
        host.appendChild(overlayWindow);
        return overlayWindow;
    }
    _createDOMPortalOutlet(window) {
        return new DomPortalOutlet(window, this._componentFactoryResolver, this._applicationRef, this._injector);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayService, deps: [{ token: OverlayContainerService }, { token: i2.OverlayKeyboardDispatcher }, { token: i0.ApplicationRef }, { token: i0.NgZone }, { token: i0.Injector }, { token: i2.OverlayPositionBuilder }, { token: i0.ComponentFactoryResolver }, { token: i0.RendererFactory2 }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: OverlayContainerService }, { type: i2.OverlayKeyboardDispatcher }, { type: i0.ApplicationRef }, { type: i0.NgZone }, { type: i0.Injector }, { type: i2.OverlayPositionBuilder }, { type: i0.ComponentFactoryResolver }, { type: i0.RendererFactory2 }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * PanelOrigin: Reference relative to which to position the panel.
 *
 * Used in conjunction with Material CDK's PositionStrategy service.
 * https://material.angular.io/components/autocomplete/api#MatAutocompleteTrigger
 */
class PanelOrigin {
    constructor(
    /** Reference to the element on which the directive is applied. */
    elementRef) {
        this.elementRef = elementRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PanelOrigin, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: PanelOrigin, selector: "[panelOrigin]", exportAs: ["uxdPanelOrigin"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PanelOrigin, decorators: [{
            type: Directive,
            args: [{
                    selector: '[panelOrigin]',
                    exportAs: 'uxdPanelOrigin'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });
/**
 * OverlayPanelComponent: A simply floating panel that
 * positions itself absolutely in the window, relative to the panelOrigin.
 */
class AntOverlayPanelComponent {
    /** Whether or not the autocomplete panel is open. */
    get panelOpen() {
        return this._overlayAttached && this.showPanel;
    }
    constructor(_element, _overlay, _viewContainerRef, _zone, _document) {
        this._element = _element;
        this._overlay = _overlay;
        this._viewContainerRef = _viewContainerRef;
        this._zone = _zone;
        this._document = _document;
        /** Position of the panel relative to the origin element  */
        this.position = 'auto';
        this._overlayAttached = false;
        this.showPanel = false;
        this._closeKeyEventStream = new Subject();
    }
    // tslint:disable-next-line:no-empty
    ngOnInit() { }
    /** Create and open the panel  */
    openPanel() {
        this._attachOverlay();
        this.showPanel = true;
    }
    /** Closes the panel. */
    closePanel() {
        if (!this._overlayAttached) {
            return;
        }
        this._overlayAttached = false;
        if (this._overlayRef && this._overlayRef.hasAttached()) {
            this._overlayRef.detach();
            this._closingActionsSubscription.unsubscribe();
        }
    }
    /** Returns a stream of actions that should close the panel */
    get panelClosingActions() {
        return merge(this._closeKeyEventStream).pipe(map((event) => {
            return event;
        }));
    }
    /** Stream of clicks outside of the autocomplete panel. */
    _getOutsideClickStream() {
        if (!this._document) {
            return from(null);
        }
        return merge(fromEvent(this._document, 'click'), fromEvent(this._document, 'touchend')).pipe(filter((event) => {
            const clickTarget = event.target;
            const panelElement = this._element.nativeElement;
            return this._overlayAttached && (clickTarget !== panelElement && !panelElement.contains(clickTarget)) && (!!this._overlayRef && !this._overlayRef.overlayElement.contains(clickTarget));
        }));
    }
    _getOverlayConfig() {
        return new OverlayConfig({
            positionStrategy: this._getOverlayPosition(),
            width: this._getPanelWidth(),
            direction: 'ltr'
        });
    }
    _createOverlay() {
        return this._overlay.create(this._getOverlayConfig());
    }
    updatePosition() {
        if (this._overlayRef.hasAttached()) {
            this._overlayRef.updatePosition();
        }
    }
    _attachOverlay() {
        let overlayRef = this._overlayRef;
        if (!overlayRef) {
            this._portal = new TemplatePortal(this.template, this._viewContainerRef);
            overlayRef = this._createOverlay();
            this._overlayRef = overlayRef;
            this._backdropClicks = this._overlayRef.backdropClick();
            overlayRef.keydownEvents().subscribe((event) => {
                // Close when pressing ESCAPE or ALT + UP_ARROW, based on the a11y guidelines.
                // See: https://www.w3.org/TR/wai-aria-practices-1.1/#textbox-keyboard-interaction
                if (event.keyCode === ESCAPE || (event.keyCode === ARROW_UP && event.altKey)) {
                    // this._resetActiveItem();
                    this._closeKeyEventStream.next();
                    // We need to stop propagation, otherwise the event will eventually
                    // reach the input itself and cause the overlay to be reopened.
                    event.stopPropagation();
                    event.preventDefault();
                }
            });
        }
        if (overlayRef && !overlayRef.hasAttached()) {
            overlayRef.attach(this._portal, this._getOverlayPosition());
            this._closingActionsSubscription = this._subscribeToClosingActions();
            const pos = overlayRef._positioner;
            pos.apply();
            this._overlayAttached = true;
        }
    }
    _subscribeToClosingActions() {
        const firstStable = this._zone.onStable.asObservable().pipe(take(1));
        return firstStable
            .pipe(switchMap(() => {
            return this.panelClosingActions;
        }), take(1))
            .subscribe((event) => {
            this.closePanel();
        });
    }
    _getOverlayPosition() {
        const strategy = this._overlay
            .position()
            .flexibleConnectedTo(this._getConnectedElement())
            .withFlexibleDimensions(false)
            .withPush(false);
        this._setStrategyPositions(strategy);
        this._positioner = strategy;
        return strategy;
    }
    _getConnectedElement() {
        if (this.connectedTo) {
            return this.connectedTo.elementRef;
        }
        return null;
    }
    _getPanelWidth() {
        return this.panelWidth || this._getHostWidth();
    }
    /** Returns the width of the input element, so the panel width can match it. */
    _getHostWidth() {
        return this._getConnectedElement().nativeElement.getBoundingClientRect().width;
    }
    /** Sets the positions on a position strategy based on the directive's input state. */
    _setStrategyPositions(positionStrategy) {
        const belowPosition = {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
        };
        const abovePosition = {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
        };
        let positions;
        if (this.position === 'above') {
            positions = [abovePosition];
        }
        else if (this.position === 'below') {
            positions = [belowPosition];
        }
        else {
            positions = [belowPosition, abovePosition];
        }
        positionStrategy.withPositions(positions);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntOverlayPanelComponent, deps: [{ token: i0.ElementRef }, { token: OverlayService }, { token: i0.ViewContainerRef }, { token: i0.NgZone }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AntOverlayPanelComponent, selector: "ant-overlay-panel", inputs: { position: "position", connectedTo: "connectedTo", panelWidth: ["width", "panelWidth"] }, queries: [{ propertyName: "template", first: true, predicate: TemplateRef, descendants: true, read: TemplateRef, static: true }], exportAs: ["antOverlayPanel"], ngImport: i0, template: `<ng-content></ng-content>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntOverlayPanelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ant-overlay-panel',
                    template: `<ng-content></ng-content>`,
                    exportAs: 'antOverlayPanel'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: OverlayService }, { type: i0.ViewContainerRef }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }, {
                    type: Optional
                }] }]; }, propDecorators: { position: [{
                type: Input,
                args: ['position']
            }], connectedTo: [{
                type: Input
            }], panelWidth: [{
                type: Input,
                args: ['width']
            }], template: [{
                type: ContentChild,
                args: [TemplateRef, { static: true, read: TemplateRef }]
            }] } });

class OverlayModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: OverlayModule, declarations: [AntOverlayPanelComponent, PanelOrigin], imports: [CommonModule, PortalModule], exports: [AntOverlayPanelComponent, PanelOrigin] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayModule, providers: [OverlayContainerService, OverlayService], imports: [CommonModule, PortalModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PortalModule],
                    declarations: [AntOverlayPanelComponent, PanelOrigin],
                    exports: [AntOverlayPanelComponent, PanelOrigin],
                    providers: [OverlayContainerService, OverlayService]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AntOverlayPanelComponent, OverlayContainerService, OverlayModule, OverlayRef, OverlayService, PanelOrigin, coerceCssPixelValue };
